import { CallState } from "@b2bportal/core-types";
import { handleAsyncThunkCacheable } from "@b2bportal/core-utilities";
import {
  type RewardsAccountResponse,
  RewardsAccountResponseEnum,
} from "@b2bportal/rbc-accounts-api";
import { createSlice } from "@reduxjs/toolkit";
import {
  type AvailablePointsResponse,
  fetchPointsBalance,
} from "./thunks/fetchPointsBalance";
import type {
  PointsBalanceType,
  PointsBalanceState,
  AccountStateType,
  AccountState,
} from "./types";

export type {
  PointsBalanceType,
  PointsBalanceState,
  AccountStateType,
  AccountState,
};

const initialAccountState: AccountStateType = {
  pointsBalance: {
    resource: {
      state: CallState.NotCalled,
    },
  },
  isFixedPointsPricingEligible: undefined,
};

export const accountInfoSlice = createSlice({
  name: "accountInfoSlice",
  initialState: initialAccountState,
  reducers: {
    clearError: (state) => {
      delete state.pointsBalance.error;
    },
  },
  extraReducers: (builder) => {
    handleAsyncThunkCacheable(builder, fetchPointsBalance, {
      get: (state) => state.pointsBalance.resource.data,
      extract: responseToPointsBalance,
      set: (state, resource) => {
        state.pointsBalance.resource = resource;
        if (resource.state === CallState.Failed) {
          const e = resource.error.error as RewardsAccountResponse;
          if (
            e?.RewardsAccountResponse ===
            RewardsAccountResponseEnum.NotAvailable
          ) {
            state.pointsBalance.error = e;
            state.isFixedPointsPricingEligible = false;
          }
        } else if (resource.state === CallState.Success) {
          state.isFixedPointsPricingEligible =
            resource.data?.isFixedPointsPricingEligible;
        }
      },
    });
  },
});

export const AccountInfoActions = accountInfoSlice.actions;
export default accountInfoSlice.reducer;

const responseToPointsBalance = (
  response: AvailablePointsResponse
): PointsBalanceType => {
  const {
    basePointsToCashMultiplier,
    businessClassPointsToCashMultiplier,
    gridOverageAndTaxesPointsToCashMultiplier,
    pointsBalance,
    rewardsAccountId,
    cardProductCode,
    isFixedPointsPricingEligible,
    minimumRedemptionPoints,
  } = response;
  return {
    totalPoints: pointsBalance,
    basePointsToCashMultiplier,
    businessClassPointsToCashMultiplier,
    gridOverageAndTaxesPointsToCashMultiplier,
    rewardsAccountId,
    cardProductCode,
    isFixedPointsPricingEligible,
    minimumRedemptionPoints,
  };
};
